import { cn } from "../lib/cn";

type ButtonVariant = "primary" | "accent" | "outline" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

// Disabled filled variants gain a ruled edge: `bg-recess` is only 1.13:1 off the card behind it.
const variantBase: Record<ButtonVariant, string> = {
  primary:
    "bg-ink text-ink-inverse disabled:border-rule disabled:bg-recess disabled:text-faint",
  accent:
    "bg-accent text-ink-inverse disabled:border-rule disabled:bg-recess disabled:text-faint",
  outline:
    "bg-card border-rule text-ink disabled:bg-recess disabled:text-faint",
  ghost: "bg-transparent text-ink-body disabled:text-faint",
};

// Ghost has no surface to lift, so it takes a translucent wash.
const variantHover: Record<ButtonVariant, string> = {
  primary: "hover:shadow-lift-lg hover:-translate-y-px",
  accent: "hover:shadow-lift-lg hover:-translate-y-px",
  outline: "hover:shadow-lift-lg hover:-translate-y-px",
  ghost: "hover:bg-smudge",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "gap-1.5 px-2.5 py-1 text-sm",
  md: "gap-2 px-3.5 py-1.5 text-sm",
  lg: "gap-2 px-5 py-2.5 text-base",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** `accent` is kept for the single act a screen is about. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Disables the button, sets `aria-busy`, and shows an inline spinner. */
  busy?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

export function Button({
  variant = "primary",
  size = "md",
  busy = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  const inert = Boolean(disabled) || busy;

  return (
    <button
      type={type}
      disabled={inert}
      aria-busy={busy || undefined}
      className={cn(
        "inline-flex select-none items-center justify-center rounded-paper font-body font-medium",
        // Every variant carries the border so all measure the same.
        "border border-transparent",
        "transition duration-150 ease-paper",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "enabled:cursor-pointer disabled:cursor-default",
        sizeClasses[size],
        variantBase[variant],
        !inert && variantHover[variant],
        className,
      )}
      {...rest}
    >
      {busy && (
        <>
          <span
            aria-hidden="true"
            className={cn(
              "size-3.5 shrink-0 animate-spin rounded-full",
              "border-[1.5px] border-current border-t-transparent",
              "motion-reduce:hidden",
            )}
          />
          <span
            aria-hidden="true"
            className="hidden font-mono leading-none motion-reduce:inline"
          >
            ...
          </span>
        </>
      )}
      {children}
    </button>
  );
}
